import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import { isAreaDeleted } from "../../hooks/AreaHook";

// Pale green used for areas created today (matches the table row highlight).
const NEW_TAB_BACKGROUND = "#E8F5E9";

/**
 * AreaTabsPreview
 * Read-only preview of how areas appear as tabs on the Organization page in
 * the app: active, non-deleted areas in position order, left to right.
 * Not interactive.
 */
const AreaTabsPreview = ({ areas }) => {
  const tabs = useMemo(
    () =>
      (areas || [])
        .filter((area) => area?.isActive === true && !isAreaDeleted(area))
        .slice()
        .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)),
    [areas]
  );

  // Counts cover non-deleted areas only (deleted areas never appear in the app).
  const counts = useMemo(() => {
    const nonDeleted = (areas || []).filter((area) => !isAreaDeleted(area));
    const active = nonDeleted.filter((area) => area?.isActive === true).length;
    return {
      active,
      inactive: nonDeleted.length - active,
      total: nonDeleted.length,
    };
  }, [areas]);

  const isCreatedToday = (area) =>
    !!area?.createdAt?.seconds &&
    moment.unix(area.createdAt.seconds).isSame(moment(), "day");

  return (
    <Box sx={containerStyles} aria-hidden="true">
      <Typography sx={titleStyles}>
        Preview of tabs on Organization Page{" "}
        <span style={countStyles}>
          ({counts.active} Active / {counts.inactive} Inactive / {counts.total}{" "}
          Total)
        </span>
      </Typography>
      {tabs.length === 0 ? (
        <Typography sx={emptyStyles}>No active areas to display.</Typography>
      ) : (
        <Box sx={scrollRowStyles}>
          {tabs.map((area) => (
            <Box
              key={area.id}
              sx={{
                ...bubbleStyles,
                background: isCreatedToday(area)
                  ? NEW_TAB_BACKGROUND
                  : "#E8E8E8",
              }}
            >
              {area.name}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AreaTabsPreview;

const containerStyles = {
  marginTop: "20px",
  paddingLeft: "16px",
  width: "100%",
  maxWidth: "calc(90vw - 120px)",
  userSelect: "none",
};

const titleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  color: "#020303",
  marginBottom: "10px",
};

const countStyles = {
  fontWeight: 400,
  color: "#515563",
};

const scrollRowStyles = {
  display: "flex",
  flexWrap: "nowrap",
  gap: "16px",
  overflowX: "auto",
  paddingBottom: "8px",
};

const bubbleStyles = {
  flex: "0 0 auto",
  whiteSpace: "nowrap",
  padding: "6px 24px",
  borderRadius: "20px",
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
  pointerEvents: "none",
};

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
};
